import { Fragment, PureComponent, ReactNode } from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import getHEOnboardingProfile, { Staff } from '../../api/getHEOnboardingProfile';
import setHEOnboardingProfile from '../../api/setHEOnboardingProfile';
import { GO_TO_APPOINTMENTS, GO_TO_HE_ONBOARDING } from '../../routes/paths';
import { getSelectedClinicId } from '../../store/idbHandler';
import { FetchingLoader, Footer, Header } from './Utils';
import { captureError, captureErrorString } from '../captureError';
import { Checkbox, Col, notification } from 'antd';
import fetchStaff, { StaffData } from '../../api/fetchStaff';
import { CheckboxValueType } from 'antd/lib/checkbox/Group';
import StaffUPIIDDetails from './StaffUPIIDDetails';
import { HEOnboardingState, heOnboardingStepsArray } from './HEOnboarding';
import { track } from '../../tracking';
import addBankAccount from '../../api/addBankAccount';
import getValidationStatus from '../../api/getValidationStatus';
import ValidatedBankDetails from './ValidatedBankDetails';
import markBankAccountValidated from '../../api/markBankAccountValidated';

export interface StaffStateData extends StaffData {
    upi_id?: string;
    isAllowed?: boolean;
    fund_acc_id?: string;
}

interface State {
    isFetchingProfile?: boolean;
    staffList: StaffStateData[];
    checkedValue: CheckboxValueType[];
    isFetchingStaff?: boolean;
    isSavingProfile?: boolean;
    staffBankData?: Staff[];
    isVerifyingBankDetails?: boolean;
    cur_soid?: string;
    fund_acc_id?: string;
    validatedBankDetails?: {
        soid: string;
        upi_id: string;
        registered_name: string;
    };
    isMarkBankAccountValidatedLoading?: boolean;
}

class StaffPage extends PureComponent<RouteComponentProps, State> {
    state: State = {
        isFetchingProfile: true,
        isFetchingStaff: true,
        staffList: [],
        checkedValue: [],
        staffBankData: [],
    };
    polling?: NodeJS.Timeout = undefined;
    POLL_INTERVAL_MS = 10 * 1000;
    currentIndex = 3;

    componentDidMount = async (): Promise<void> => {
        try {
            const { success, profile, error = '' } = await getHEOnboardingProfile();
            if (success && profile) {
                const { staff } = profile;
                this.fetchStaff(staff);
            } else {
                captureErrorString(error);
                track('ERROR', { on: 'getHEOnboardingProfile', what: error });
            }
        } catch (error) {
            captureError(error as Error);
            track('ERROR', {
                on: 'getHEOnboardingProfile',
                error: (error as Error).message,
            });
        } finally {
            this.setState({ isFetchingProfile: false });
        }
    };

    fetchStaff = async (staff?: Staff[]) => {
        try {
            const { data = [] } = await fetchStaff();
            const staffList = data.map((i) => {
                const currentDetails = staff?.find((j) => j.staff_oid === i.soid);
                if (currentDetails) {
                    const { bank_details = {}, isAllowed } = currentDetails;
                    const { upi_id, fund_acc_id } = bank_details;
                    return { ...i, upi_id, isAllowed, fund_acc_id };
                }
                return i;
            });
            this.setState({
                staffList,
                checkedValue: staffList
                    .filter((i) => (i as StaffStateData).isAllowed)
                    .map((i) => i.soid),
            });
        } catch (error) {
            captureError(error as Error);
            track('ERROR', {
                on: 'fetchStaff',
                error: (error as Error).message,
            });
            notification.error({
                message: 'Error while fetching details',
                duration: 5,
            });
        } finally {
            this.setState({
                isFetchingStaff: false,
            });
        }
    };

    saveDetailsHandler = async (lastStep: HEOnboardingState) => {
        const { staffList } = this.state;
        this.setState({
            isSavingProfile: true,
        });
        await setHEOnboardingProfile({
            staff: staffList.map(({ isAllowed, soid, upi_id, fund_acc_id }) => ({
                isAllowed,
                staff_oid: soid,
                bank_details: { upi_id, fund_acc_id },
            })),
            lastStep,
        });
        this.setState({
            isSavingProfile: false,
        });
    };

    onClickHandler = async () => {
        const { history } = this.props;
        const { saveDetailsHandler, currentIndex } = this;
        try {
            await saveDetailsHandler(heOnboardingStepsArray[currentIndex + 1]);
            history.replace(GO_TO_HE_ONBOARDING(heOnboardingStepsArray[currentIndex + 1]));
        } catch (err) {
            captureError(err as Error);
            track('ERROR', {
                on: 'setHEOnboardingProfile',
                error: (err as Error).message,
            });
            notification.error({
                message: 'Error while saving details!',
                duration: 5,
            });
        }
    };

    saveAndExitHandler = async () => {
        const { history } = this.props;
        const { saveDetailsHandler, currentIndex } = this;
        try {
            await saveDetailsHandler(heOnboardingStepsArray[currentIndex]);
            history.replace(GO_TO_APPOINTMENTS(getSelectedClinicId() || ''));
        } catch (err) {
            captureError(err as Error);
            notification.error({
                message: 'Error while saving details!',
                duration: 5,
            });
        }
    };

    onChange = (checkedValue: CheckboxValueType[]): void => {
        const { staffList } = this.state;
        const newStaffList = staffList.map((i) => {
            return { ...i, isAllowed: checkedValue.includes(i.soid) };
        });
        this.setState({
            checkedValue,
            staffList: newStaffList,
        });
    };

    onInputChange = (soid: string, upi_id: string): void => {
        const { staffList } = this.state;
        const newStaffList = staffList.map((i) => (i.soid === soid ? { ...i, upi_id } : i));
        this.setState({ staffList: newStaffList });
    };

    onBackHandler = (): void => {
        const { history } = this.props;
        const { currentIndex } = this;
        history.replace(GO_TO_HE_ONBOARDING(heOnboardingStepsArray[currentIndex - 1]));
    };

    onUPIDetailsHandler = async (staffData: StaffStateData) => {
        this.setState({
            isVerifyingBankDetails: true,
            cur_soid: staffData.soid,
        });
        const { soid, upi_id } = staffData;
        const { success, response } = await addBankAccount({
            account_type: 'vpa',
            account_details: {
                address: upi_id,
            },
            oid: soid,
        });
        const { error } = response;
        if (success) {
            const { fund_acc_id } = response;
            if (fund_acc_id && upi_id) {
                this.poll(fund_acc_id, upi_id, soid);
                this.polling = setInterval(
                    () => this.poll(fund_acc_id, upi_id, soid),
                    this.POLL_INTERVAL_MS,
                );
            }
            track('UPI_ID_DETAILS_VERIFIED', { fund_acc_id, upi_id });
        } else {
            notification.error({
                message: error?.description || 'Error while validating upi id details!',
                duration: 5,
            });
            track('ERROR', {
                on: 'verifyUpiIdDetails',
                error: error?.description,
            });
            this.setState({
                isVerifyingBankDetails: false,
                cur_soid: undefined,
            });
        }
    };

    poll = async (fund_acc_id: string, upi_id: string, soid: string) => {
        try {
            const { response } = await getValidationStatus(fund_acc_id);
            const { validation_details } = response;
            const { registered_name } = validation_details;

            if (response.status === 'validation_completed' || response.status === 'validated') {
                if (this.polling) {
                    clearInterval(this.polling);
                }
                if (!registered_name) {
                    notification.error({
                        message: 'Please enter valid upi id details!',
                        duration: 5,
                    });
                    this.setState({
                        isVerifyingBankDetails: false,
                    });
                    return;
                }
                this.setState({
                    isVerifyingBankDetails: false,
                    validatedBankDetails: {
                        soid,
                        upi_id,
                        registered_name,
                    },
                    fund_acc_id,
                });
            }
        } catch (err) {
            captureError(err as Error);
            track('ERROR', {
                on: 'getValidationStatus',
                error: (err as Error).message,
            });
            notification.error({
                message: 'Error while validating upi id details!',
                duration: 5,
            });
        }
    };

    onConfirm = async () => {
        const { validatedBankDetails, fund_acc_id, staffList } = this.state;
        if (!validatedBankDetails || !fund_acc_id) {
            notification.error({
                message: 'Error while validating details!',
                duration: 5,
            });
            return;
        }
        const { soid } = validatedBankDetails;

        this.setState({
            isMarkBankAccountValidatedLoading: true,
        });

        try {
            const { response } = await markBankAccountValidated(fund_acc_id);
            if (response.success) {
                this.setState({
                    staffList: staffList.map((i) => (i.soid === soid ? { ...i, fund_acc_id } : i)),
                    validatedBankDetails: undefined,
                });
            } else {
                notification.error({
                    message: 'Error while validating details!',
                    duration: 5,
                });
            }
        } catch (err) {
            captureError(err as Error);
            track('ERROR', {
                on: 'markBankAccountValidated',
                error: (err as Error).message,
            });
            notification.error({
                message: 'Error while validating details!',
                duration: 5,
            });
        } finally {
            this.setState({
                isMarkBankAccountValidatedLoading: false,
            });
        }
    };

    render(): ReactNode {
        const {
            isFetchingProfile,
            isSavingProfile,
            isFetchingStaff,
            staffList,
            checkedValue,
            validatedBankDetails,
            isVerifyingBankDetails,
            cur_soid,
            isMarkBankAccountValidatedLoading,
        } = this.state;
        const {
            onClickHandler,
            saveAndExitHandler,
            onChange,
            onInputChange,
            onBackHandler,
            currentIndex,
            onUPIDetailsHandler,
            onConfirm,
        } = this;
        const checkedValueSet = new Set(checkedValue);

        const disabled = isFetchingProfile || isSavingProfile || isVerifyingBankDetails;

        return (
            <Fragment>
                <FetchingLoader isFetchingProfile={isFetchingProfile} />
                <div className="flex flex-col h-full w-full justify-center">
                    <Header
                        saveAndExitHandler={saveAndExitHandler}
                        disabled={disabled}
                        isSavingProfile={isSavingProfile}
                    />

                    <div className="flex-1 overflow-scroll hide-scrollbar w-592 m-auto pb-32">
                        <div className="space-y-8 mb-24">
                            <p className="text-24 font-700">Final step, enroll your staff</p>
                            <p className="text-darwin-neutral-900">
                                Staff gets an incentive of upto <span className="font-700">2%</span>{' '}
                                of order value for every order delivered.
                            </p>
                        </div>
                        <p className="mb-16">
                            Select staff(s) who will help placing order for the patients (optional)
                        </p>

                        {isFetchingStaff ? (
                            <div className="flex items-center space-x-8">
                                <svg
                                    className="w-16 h-16 fill-darwin-primary-500 animate-spin"
                                    role="img"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 512 512"
                                >
                                    <path d="M96 256c0-26.5-21.5-48-48-48S0 229.5 0 256s21.5 48 48 48S96 282.5 96 256zM108.9 60.89c-26.5 0-48.01 21.49-48.01 47.99S82.39 156.9 108.9 156.9s47.99-21.51 47.99-48.01S135.4 60.89 108.9 60.89zM108.9 355.1c-26.5 0-48.01 21.51-48.01 48.01S82.39 451.1 108.9 451.1s47.99-21.49 47.99-47.99S135.4 355.1 108.9 355.1zM256 416c-26.5 0-48 21.5-48 48S229.5 512 256 512s48-21.5 48-48S282.5 416 256 416zM464 208C437.5 208 416 229.5 416 256s21.5 48 48 48S512 282.5 512 256S490.5 208 464 208zM403.1 355.1c-26.5 0-47.99 21.51-47.99 48.01S376.6 451.1 403.1 451.1s48.01-21.49 48.01-47.99S429.6 355.1 403.1 355.1zM256 0C229.5 0 208 21.5 208 48S229.5 96 256 96s48-21.5 48-48S282.5 0 256 0z"></path>
                                </svg>
                                <p>Fetching staff list...</p>
                            </div>
                        ) : staffList.length !== 0 ? (
                            <Checkbox.Group
                                style={{ width: '100%' }}
                                onChange={onChange}
                                value={checkedValue}
                            >
                                {staffList.map((i) => (
                                    <Col className="mb-16 space-y-12">
                                        <Checkbox value={i.soid}>{i.fln}</Checkbox>
                                        {checkedValueSet.has(i.soid) && (
                                            <StaffUPIIDDetails
                                                staffData={i}
                                                onInputChange={onInputChange}
                                                onUPIDetailsHandler={onUPIDetailsHandler}
                                                isVerifyingBankDetails={isVerifyingBankDetails}
                                                cur_soid={cur_soid}
                                            />
                                        )}
                                    </Col>
                                ))}
                            </Checkbox.Group>
                        ) : (
                            <div className="space-y-5">
                                <p>We could not find any staff associated with this account.</p>
                                <p>
                                    To add a new staff for clinic commerce, reach out to us via chat
                                    once you complete enrolling your details here.
                                </p>
                            </div>
                        )}
                    </div>

                    <Footer
                        onClickHandler={onClickHandler}
                        onBackHandler={onBackHandler}
                        disabled={disabled}
                        isSavingProfile={isSavingProfile}
                        currentIndex={currentIndex}
                        buttonText={'Get Started'}
                    />
                </div>

                {validatedBankDetails && (
                    <ValidatedBankDetails
                        validatedBankDetails={validatedBankDetails}
                        onClose={() => this.setState({ validatedBankDetails: undefined })}
                        onConfirm={onConfirm}
                        isSavingProfile={isMarkBankAccountValidatedLoading}
                    />
                )}
            </Fragment>
        );
    }
}

export default connect()(withRouter(StaffPage));
